import { Flex, useMantineTheme, Text, Box, ScrollArea } from "@mantine/core";
import { useContext, useEffect, useRef } from "react";
import { AppContext } from "src/api/AppContext";
import { useForm, yupResolver } from "@mantine/form";
import { CLIENT_TAB_LIST, CardRadius, HeightCard } from "src/utils/constants";
import useAppMain from "src/hooks/useAppMain";
import { useHeaderContext } from "src/api/context/HeaderContext";
import { MdOutlineSave } from "react-icons/md";
import FormCard from "src/components/FormCard";
import TextInput from "src/components/TextInput/TextInput";
import NumberInput from "src/components/NumberInput/NumberInput";
import Select from "src/components/Select";
import { object, string } from "yup";
import {
  Gender,
  NotificationType,
  useGetOrganizationQuery,
  useUpdateClientMutation,
  useUpdateUserMutation,
} from "src/generated/graphql";
import ImageUpload from "src/clientComponents/ImageUpload";
import ActionCard from "src/components/ActionCard";

interface IProfileFormPayload {
  firstName: string;
  middleName: string;
  lastName: string;
  phoneNumber: number;
  profilePicture: string | ArrayBuffer;
  smsConsent: boolean;
  defaultLocation: string | null;
  notificationType: NotificationType;
  // New fields
  homePhone?: string;
  workPhone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  idNumber?: string;
  employer?: string;
  gender?: undefined;
  emergencyName?: string;
  emergencyPhone?: string;
  relationship?: string;
}

const profileFormSchema = object().shape({
  firstName: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "First Name must be at least 2 letters")
    .required("First Name is required")
    .max(30, "First Name must not be greater than 30 letters"),
  middleName: string()
    .trim("No spaces allowed")
    .strict(true)
    .matches(/.{2,}/, {
      excludeEmptyString: true,
      message: "Middle Name must be at least 2 characters",
    })
    .max(30, "Middle Name must not be greater than 30 letters"),
  lastName: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Last Name must be at least 2 letters")
    .required("Last Name is required")
    .max(30, "Last Name must not be greater than 30 letters"),
  phoneNumber: string().matches(/^(?:\+\d{9,11}|\d{10,11})?$/, {
    excludeEmptyString: true,
    message: "Enter a valid phone number",
  }),

  // New fields
  homePhone: string()
    .matches(/^(?:\+\d{9,11}|\d{10,11})?$/, {
      excludeEmptyString: true,
      message: "Enter a valid phone number",
    })
    .notRequired(),
  workPhone: string()
    .matches(/^(?:\+\d{9,11}|\d{10,11})?$/, {
      excludeEmptyString: true,
      message: "Enter a valid phone number",
    })
    .notRequired(),
  address: string().max(200).notRequired(),
  city: string().max(50).notRequired(),
  state: string().max(50).notRequired(),
  zipCode: string().max(50).notRequired(),
  country: string().max(50).notRequired(),
  idNumber: string().max(30).notRequired(),
  employer: string().max(30).notRequired(),
  emergencyName: string().max(60).notRequired(),
  emergencyPhone: string()
    .matches(/^(?:\+\d{9,11}|\d{10,11})?$/, {
      excludeEmptyString: true,
      message: "Enter a valid phone number",
    })
    .notRequired(),
  relationship: string().max(20).notRequired(),
});

const EditProfile = () => {
  const { currentUser, currentClient, refetchUserData } =
    useContext(AppContext);
  const { updateHeaderContent } = useHeaderContext();
  const theme = useMantineTheme();
  const { data: organizationData } = useGetOrganizationQuery({
    variables: {
      id: currentClient?.organization?.id || "",
    },
  });

  useAppMain(CLIENT_TAB_LIST.EDIT_PROFILE, true);
  useEffect(() => {
    updateHeaderContent(
      <MdOutlineSave
        size={25}
        onClick={() => triggerSubmit()}
        color={theme.colors.secondaryTextColor[6]}
      />
    );
  }, []);

  const refSubmitButtom = useRef<HTMLButtonElement>(null);
  const triggerSubmit = () => {
    refSubmitButtom?.current?.click();
  };

  const form = useForm({
    initialValues: {
      email: currentUser?.email,
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
      middleName: currentUser?.middleName,
      phoneNumber: currentUser?.phoneNumber
        ? Number(currentUser?.phoneNumber)
        : undefined,
      profilePicture: currentUser?.profilePicUrl,
      smsConsent: currentUser?.smsConsent,
      defaultLocation: currentClient?.defaultLocation?.id || null,
      notificationType:
        currentClient?.preferences?.notificationType || NotificationType.None,
      //New fields
      homePhone: currentClient?.homePhone,
      workPhone: currentClient?.workPhone,
      address: currentClient?.address,
      city: currentClient?.city,
      state: currentClient?.state,
      country: currentClient?.country,
      zipCode: currentClient?.zipCode,
      idNumber: currentClient?.idNumber,
      employer: currentClient?.employer,
      gender: currentClient?.gender || undefined,
      emergencyName: currentClient?.emergencyName,
      emergencyPhone: currentClient?.emergencyPhone,
      relationship: currentClient?.relationship,
    } as IProfileFormPayload,
    validate: yupResolver(profileFormSchema),
  });

  const [updateProfile, { loading: profileLoading }] = useUpdateUserMutation({
    onCompleted: () => {
      refetchUserData();
      form.resetDirty();
    },
  });

  const [updateClientLocation, { loading: locationLoading }] =
    useUpdateClientMutation({
      onCompleted: () => {
        refetchUserData();
        form.resetDirty();
      },
    });

  const handleProfileUpdate = async (payload: IProfileFormPayload) => {
    try {
      const {
        profilePicture,
        phoneNumber,
        defaultLocation,
        notificationType,
        //Client
        homePhone,
        workPhone,
        address,
        city,
        state,
        country,
        zipCode,
        idNumber,
        employer,
        gender,
        emergencyName,
        emergencyPhone,
        relationship,
        ...restPayload
      } = payload;
      updateProfile({
        variables: {
          data: {
            profilePic: profilePicture as string,
            phoneNumber: phoneNumber ? `+${phoneNumber}` : undefined,
            preferences: { notificationType },
            clientData: {
              homePhone,
              workPhone,
              address,
              city,
              state,
              country,
              zipCode,
              idNumber,
              employer,
              gender,
              emergencyName,
              emergencyPhone,
              relationship,
            },
            ...restPayload,
          },
          id: currentUser?.id as string,
        },
      });

      payload.defaultLocation &&
        updateClientLocation({
          variables: {
            id: currentClient?.id as string,
            data: {
              defaultLocation: { id: payload.defaultLocation },
            },
          },
        });
    } catch (e: any) {
      console.error("Error: ", e.response);
    }
  };

  const setImageFile = (file: string | ArrayBuffer) => {
    form.setFieldValue("profilePicture", file); // by using base64
  };

  // form field props
  const { value: imageSrcValue, ...profilePicProps } =
    form.getInputProps("profilePicture");

  const { value: smsConsentValue, ...smsConsentProps } =
    form.getInputProps("smsConsent");

  const { value: phoneNumberValue, ...phoneNumberProps } =
    form.getInputProps("phoneNumber");

  const { value: defaultLocationValue, ...defaultLocationProps } =
    form.getInputProps("defaultLocation");
  const isLoading = profileLoading || locationLoading;

  return (
    <FormCard
      title={"Profile"}
      loading={isLoading}
      onSave={form.onSubmit((values) => handleProfileUpdate(values))}
      hideCard={true}
    >
      <ScrollArea.Autosize maxHeight={"85vh"} offsetScrollbars>
        <Flex direction={"column"} justify={"center"} w={"100%"}>
          <Flex justify={"center"}>
            <ImageUpload
              onReadSuccess={setImageFile}
              src={imageSrcValue}
              iconSize={25}
              alt="Profile Image"
              avatar={true}
              style={{
                height: "150px",
                width: "150px",
                objectFit: "cover",
                aspectRatio: "1/1",
                borderRadius: "50%",
                justifyContent: "center",
                alignItems: "center",
              }}
              disabled={isLoading}
              {...profilePicProps}
            />
          </Flex>

          <div
            style={{
              padding: "0 15px",
              borderRadius: 4,
              marginTop: 15,
            }}
          >
            <Text fw={500} size={18}>
              Basic Info
            </Text>
            <TextInput
              mt={"xs"}
              //labelColor={theme.colors.secondaryTextColor[2]}
              withAsterisk
              label="First Name"
              disabled={isLoading}
              {...form.getInputProps("firstName")}
            />
            <TextInput
              //labelColor={theme.colors.secondaryTextColor[2]}
              label="Middle Name"
              disabled={isLoading}
              {...form.getInputProps("middleName")}
            />
            <TextInput
              //labelColor={theme.colors.secondaryTextColor[2]}
              withAsterisk
              label="Last Name"
              disabled={isLoading}
              {...form.getInputProps("lastName")}
            />

            <NumberInput
              //labelColor={theme.colors.secondaryTextColor[2]}
              label="Mobile Number"
              disabled={isLoading}
              value={phoneNumberValue}
              {...phoneNumberProps}
              hideControls={true}
            />

            <TextInput
              //labelColor={theme.colors.secondaryTextColor[2]}
              withAsterisk
              label="Email"
              disabled
              {...form.getInputProps("email")}
            />

            <div>
              <Flex align="center" justify="space-between" gap={"xs"}>
                <Box sx={{ flex: 1 }}>
                  <TextInput
                    label="Home Number"
                    {...form.getInputProps("homePhone")}
                    disabled={isLoading}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextInput
                    label="Work Number"
                    {...form.getInputProps("workPhone")}
                    disabled={isLoading}
                  />
                </Box>
              </Flex>
              <TextInput
                label="Address"
                {...form.getInputProps("address")}
                disabled={isLoading}
              />
              <Flex align="center" justify="space-between" gap={"xs"}>
                <Box sx={{ flex: 1 }}>
                  <TextInput
                    label="City"
                    {...form.getInputProps("city")}
                    disabled={isLoading}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextInput
                    label="State"
                    {...form.getInputProps("state")}
                    disabled={isLoading}
                  />
                </Box>
              </Flex>
              <Flex align="center" justify="space-between" gap={"xs"}>
                <Box sx={{ flex: 1 }}>
                  <TextInput
                    width={"100%"}
                    label="Country"
                    {...form.getInputProps("country")}
                    disabled={isLoading}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextInput
                    width={"100%"}
                    label="Zip Code"
                    {...form.getInputProps("zipCode")}
                    disabled={isLoading}
                  />
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" gap={"xs"}>
                <Box sx={{ flex: 1 }}>
                  <TextInput
                    label="License/ID"
                    {...form.getInputProps("idNumber")}
                    disabled={isLoading}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Select
                    disabled={isLoading}
                    data={Object.values(Gender)}
                    label="Gender"
                    {...form.getInputProps("gender")}
                  />
                </Box>
              </Flex>
              <TextInput
                label="Employer"
                {...form.getInputProps("employer")}
                disabled={isLoading}
              />

              <ActionCard
                title="Emergency Contact"
                mt={"sm"}
                height={HeightCard.sm}
                radius={CardRadius.sm}
                size={"sm"}
                headerOnly
              >
                <TextInput
                  label="Name"
                  mt={0}
                  {...form.getInputProps("emergencyName")}
                  disabled={isLoading}
                />
                <TextInput
                  label="Phone"
                  {...form.getInputProps("emergencyPhone")}
                  disabled={isLoading}
                />
                <TextInput
                  label="Relationship"
                  {...form.getInputProps("relationship")}
                  disabled={isLoading}
                />
              </ActionCard>
            </div>

            <Text fw={500} size={18} mt={"xs"}>
              Preferences
            </Text>

            <Select
              mt={3}
              label="Default Location"
              data={
                organizationData?.organizationOne?.locations.map((item) => ({
                  value: item.id,
                  label: item.name,
                })) || []
              }
              value={defaultLocationValue}
              disabled={profileLoading || locationLoading}
              {...defaultLocationProps}
            />

            <Select
              label="Notification Preference"
              data={
                Object.values(NotificationType).map((item) => ({
                  value: item,
                  label: item,
                })) || []
              }
              {...form.getInputProps("notificationType")}
            />
            <button hidden={true} ref={refSubmitButtom} type={"submit"} />
            <Text size={12} fw={500} mt={"xs"}>
              By selecting either Email, SMS, or Both you agree to receiving
              notifications via your chosen delivery method at the email or
              phone number provided in the Basic Info section of your profile.
              You can opt out of this feature at any time.
            </Text>
          </div>
        </Flex>
      </ScrollArea.Autosize>
    </FormCard>
  );
};

export default EditProfile;
